import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type PlanTier = "free" | "plus" | "together";

export interface Subscription {
  plan: PlanTier;
  trialEndsAt: string | null;
  isTrialing: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Primary: read from local DB (fast)
  const { data, isLoading } = useQuery({
    queryKey: ["subscription", user?.id],
    queryFn: async (): Promise<Subscription> => {
      if (!user) return { plan: "free", trialEndsAt: null, isTrialing: false };

      const { data, error } = await supabase
        .from("subscriptions")
        .select("plan, trial_ends_at")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return { plan: "free", trialEndsAt: null, isTrialing: false };

      const trialEndsAt = data.trial_ends_at;
      const isTrialing = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;

      return {
        plan: data.plan as PlanTier,
        trialEndsAt,
        isTrialing,
      };
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  // Background sync with Stripe (every 60s) — skipped if staleTime hasn't elapsed
  useQuery({
    queryKey: ["subscription-sync", user?.id],
    queryFn: async () => {
      // If subscription was recently updated optimistically, skip this sync
      const subState = queryClient.getQueryState(["subscription", user?.id]);
      const dataAge = subState?.dataUpdatedAt ? Date.now() - subState.dataUpdatedAt : Infinity;
      if (dataAge < 30_000) {
        return null;
      }
      const { error } = await supabase.functions.invoke("check-subscription");
      if (error) console.warn("Subscription sync failed:", error);
      queryClient.invalidateQueries({ queryKey: ["subscription", user?.id] });
      return null;
    },
    enabled: !!user,
    staleTime: 300_000,
    refetchInterval: 300_000,
  });

  // Realtime: invalidate subscription cache on DB changes
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subscription-rt-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` }, () => {
        queryClient.invalidateQueries({ queryKey: ["subscription", user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  const plan = data?.plan ?? "free";

  return {
    plan,
    isTrialing: data?.isTrialing ?? false,
    trialEndsAt: data?.trialEndsAt ?? null,
    isLoading,
    isFree: plan === "free",
    isPlus: plan === "plus" || plan === "together",
    isTogether: plan === "together",
    maxChildren: Infinity,
    maxHistoryDays: plan === "free" ? 7 : Infinity,
    canCollaborate: plan === "together",
  };
}

/** Start a Stripe checkout session for a given plan */
export async function startCheckout(priceId: string, familyId?: string, couponCode?: string) {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: { priceId, ...(familyId ? { familyId } : {}), ...(couponCode ? { couponCode } : {}) },
  });
  if (error) throw error;
  if (data?.url) {
    window.location.href = data.url;
  }
}

/** Open the Stripe customer portal */
export async function openCustomerPortal() {
  const { data, error } = await supabase.functions.invoke("customer-portal");
  if (error) throw error;
  if (data?.url) {
    window.location.href = data.url;
  }
}
